/**
 * Icon — wrapper sobre lucide-react.
 *
 * Mantém a API antiga (`<Icon name="check" size={14} />`) pra evitar quebra
 * em todo o frontend, mas internamente renderiza ícones do lucide.
 *
 * Novos ícones devem ser adicionados em `LUCIDE_MAP` abaixo. Use os nomes
 * canônicos do https://lucide.dev/icons/ — fica fácil descobrir.
 */
import {
  AlertTriangle,
  ArrowLeft,
  ArrowRight,
  Bell,
  BookOpen,
  Box,
  Briefcase,
  Calendar,
  CheckCircle2,
  Check,
  ChevronDown,
  ChevronRight,
  Coins,
  Copy,
  Download,
  Edit3,
  ExternalLink,
  Eye,
  FileText,
  Filter,
  Globe,
  Hash,
  Heart,
  Image as ImageIcon,
  Info,
  KanbanSquare,
  Layers,
  Link as LinkIcon,
  List,
  Lock,
  LogOut,
  Mail,
  MessageCircle,
  Package,
  Paperclip,
  Pencil,
  Plug,
  Plus,
  RefreshCw,
  Rocket,
  Search,
  Send,
  Settings,
  Sparkles,
  Star,
  Tag,
  Target,
  Trash2,
  Trophy,
  Upload,
  User as UserIcon,
  Users,
  Video,
  X,
  Zap,
  type LucideProps,
} from "lucide-react";

export type IconName =
  // Pessoa / interação
  | "agent" | "user" | "users" | "bell" | "lock" | "warn"
  // Layout
  | "list" | "kanban" | "grid" | "filter" | "settings" | "eye" | "search"
  // Ação
  | "x" | "check" | "plus" | "send" | "refresh" | "edit" | "copy" | "download"
  | "upload" | "trash" | "info"
  // Navegação
  | "arrow_r" | "arrow_l" | "chevron_r" | "chevron_d" | "external" | "logout"
  // Categorias
  | "chart" | "target" | "photo" | "video" | "calendar" | "mail" | "globe"
  | "chat" | "star" | "trophy" | "coin" | "zap" | "package" | "layers"
  | "briefcase" | "box" | "tag" | "hash" | "heart" | "rocket"
  // Briefing 2.0
  | "link" | "paperclip" | "file" | "sparkles" | "alert"
  // Skill / book
  | "book"
  // ESCALA / radar
  | "pencil";

const LUCIDE_MAP: Record<IconName, React.ComponentType<LucideProps>> = {
  agent: UserIcon,
  user: UserIcon,
  users: Users,
  bell: Bell,
  lock: Lock,
  warn: AlertTriangle,
  alert: AlertTriangle,
  list: List,
  kanban: KanbanSquare,
  grid: KanbanSquare,
  filter: Filter,
  settings: Settings,
  eye: Eye,
  search: Search,
  x: X,
  check: Check,
  plus: Plus,
  send: Send,
  refresh: RefreshCw,
  edit: Edit3,
  pencil: Pencil,
  copy: Copy,
  download: Download,
  upload: Upload,
  trash: Trash2,
  info: Info,
  arrow_r: ArrowRight,
  arrow_l: ArrowLeft,
  chevron_r: ChevronRight,
  chevron_d: ChevronDown,
  external: ExternalLink,
  logout: LogOut,
  chart: KanbanSquare,
  target: Target,
  photo: ImageIcon,
  video: Video,
  calendar: Calendar,
  mail: Mail,
  globe: Globe,
  chat: MessageCircle,
  star: Star,
  trophy: Trophy,
  coin: Coins,
  zap: Zap,
  package: Package,
  layers: Layers,
  briefcase: Briefcase,
  box: Box,
  tag: Tag,
  hash: Hash,
  heart: Heart,
  rocket: Rocket,
  link: LinkIcon,
  paperclip: Paperclip,
  file: FileText,
  sparkles: Sparkles,
  book: BookOpen,
};

export function Icon({
  name,
  size = 16,
  color,
  style,
  className,
  strokeWidth = 1.75,
}: {
  name: IconName;
  size?: number;
  color?: string;
  style?: React.CSSProperties;
  className?: string;
  strokeWidth?: number;
}) {
  const LucideComponent = LUCIDE_MAP[name] ?? AlertTriangle;
  return (
    <LucideComponent
      size={size}
      color={color}
      style={style}
      className={className}
      strokeWidth={strokeWidth}
      aria-hidden="true"
    />
  );
}

// Exporta tipos auxiliares pra quem quiser construir variantes
export type { LucideProps };
// Re-export do lucide pra novos componentes que queiram ícones diretos
export {
  CheckCircle2,
  Plug,
};
